import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin } from "lucide-react";
import { BUSINESS } from "@/data/site";
import { SECTION, BTN_GOLD, INPUT_CLS, API } from "./styles";

const initial = { name: "", email: "", phone: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/messages`, form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className={`${SECTION} bg-[#0A1F3C]`}>
      <div className="grid lg:grid-cols-2 gap-14">
        <div>
          <p className="font-accent-script text-2xl md:text-3xl text-[#D4AF37] mb-3">Get In Touch</p>
          <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white gold-underline"
            data-testid="contact-headline">
            CONTACT US
          </h2>
          <p className="text-blue-100/80 mt-6 mb-12 leading-relaxed max-w-lg">
            Whether it's a question about an active project or the start of something new — we'd
            love to hear from you.
          </p>

          <div className="space-y-8">
            <div className="flex gap-5" data-testid="contact-phone-active">
              <span className="border border-[#D4AF37] p-3 h-fit"><Phone className="h-5 w-5 text-[#D4AF37]" /></span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 mb-1">
                  Questions & Concerns — Active Projects
                </p>
                <a href={BUSINESS.phoneActiveHref} className="font-serif text-2xl text-white hover:text-[#D4AF37] transition-colors">
                  {BUSINESS.phoneActive}
                </a>
              </div>
            </div>
            <div className="flex gap-5" data-testid="contact-phone-admin">
              <span className="border border-[#D4AF37] p-3 h-fit"><Phone className="h-5 w-5 text-[#D4AF37]" /></span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 mb-1">
                  Administration & Pending Projects
                </p>
                <a href={BUSINESS.phoneAdminHref} className="font-serif text-2xl text-white hover:text-[#D4AF37] transition-colors">
                  {BUSINESS.phoneAdmin}
                </a>
              </div>
            </div>
            <div className="flex gap-5" data-testid="contact-email">
              <span className="border border-[#D4AF37] p-3 h-fit"><Mail className="h-5 w-5 text-[#D4AF37]" /></span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 mb-1">Email</p>
                <a href={`mailto:${BUSINESS.email}`} className="font-serif text-2xl text-white hover:text-[#D4AF37] transition-colors break-all">
                  {BUSINESS.email}
                </a>
              </div>
            </div>
            <div className="flex gap-5" data-testid="contact-area">
              <span className="border border-[#D4AF37] p-3 h-fit"><MapPin className="h-5 w-5 text-[#D4AF37]" /></span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 mb-1">Service Area</p>
                <p className="font-serif text-2xl text-white">Central Virginia</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E2E2DE] shadow-lg p-8 md:p-10 space-y-7 h-fit"
          data-testid="contact-form">
          <h3 className="font-serif text-2xl text-[#0A1F3C]">Send Us a Message</h3>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Full Name *</label>
            <input required data-testid="contact-name-input" className={INPUT_CLS} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
          </div>
          <div className="grid md:grid-cols-2 gap-7">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Email *</label>
              <input required type="email" data-testid="contact-email-input" className={INPUT_CLS} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Phone</label>
              <input type="tel" data-testid="contact-phone-input" className={INPUT_CLS} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(804) 555-0123" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Message *</label>
            <textarea required rows={5} data-testid="contact-message-input" className={`${INPUT_CLS} resize-none`}
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How can we help?" />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit-btn"
            className={`${BTN_GOLD} disabled:opacity-60`}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};
